import { useEffect } from 'react';
import type { HeadDescriptor } from './HeadDescriptor.js';

/**
 * Aplica un `HeadDescriptor` en el cliente.
 *
 * En el primer render el `<head>` ya viene resuelto del server (`renderHead`), así que
 * esto solo importa en la navegación client-side: actualiza title/meta/canonical sin
 * recargar. Hace upsert por selector, no borra tags ajenos.
 *
 * `null` no toca nada: mientras carga la página nueva, el `<head>` de la anterior es mejor
 * que un título genérico parpadeando en la pestaña.
 */
export function useHead(head: HeadDescriptor | null): void {
  useEffect(() => {
    if (typeof document === 'undefined' || !head) return;

    document.title = head.title;

    upsertMeta('name', 'description', head.description);
    upsertMeta('name', 'robots', head.robots ?? 'index,follow');
    upsertLink('canonical', head.canonical);

    const og = head.openGraph ?? {};
    upsertMeta('property', 'og:type', og.type ?? 'website');
    upsertMeta('property', 'og:title', og.title ?? head.title);
    upsertMeta('property', 'og:description', og.description ?? head.description);
    upsertMeta('property', 'og:url', og.url ?? head.canonical);
    upsertMeta('property', 'og:image', og.image);
    upsertMeta('property', 'og:site_name', og.siteName);

    const tw = head.twitter ?? {};
    upsertMeta('name', 'twitter:card', tw.card ?? (og.image ? 'summary_large_image' : 'summary'));
    upsertMeta('name', 'twitter:title', tw.title ?? og.title ?? head.title);
    upsertMeta('name', 'twitter:description', tw.description ?? og.description ?? head.description);
    upsertMeta('name', 'twitter:image', tw.image ?? og.image);

    upsertJsonLd(head.jsonLd);
  }, [head]);
}

function upsertMeta(kind: 'name' | 'property', key: string, content: string | undefined): void {
  const selector = `meta[${kind}="${key}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  if (!content) {
    existing?.remove();
    return;
  }
  if (existing) {
    existing.content = content;
    return;
  }
  const el = document.createElement('meta');
  el.setAttribute(kind, key);
  el.content = content;
  document.head.appendChild(el);
}

function upsertLink(rel: string, href: string | undefined): void {
  const existing = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!href) {
    existing?.remove();
    return;
  }
  if (existing) {
    existing.href = href;
    return;
  }
  const el = document.createElement('link');
  el.rel = rel;
  el.href = href;
  document.head.appendChild(el);
}

/** Los bloques JSON-LD que administra el router se marcan con data-head-jsonld. */
function upsertJsonLd(blocks: HeadDescriptor['jsonLd']): void {
  for (const el of document.head.querySelectorAll('script[data-head-jsonld]')) el.remove();
  for (const block of blocks ?? []) {
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.dataset['headJsonld'] = '';
    el.textContent = JSON.stringify(block);
    document.head.appendChild(el);
  }
}
